import { useEffect, useMemo, useState } from 'react';
import schemaJson from '../filters-schema.json';
import type { FilterSchema, FilterField } from '../types';
import * as Icons from '../icons';
import { SUPPORTED_CATEGORIES, SUPPORTED_FIELDS, SUPPORTED_POWER_FILTERS } from '../supportedFields';

const rawSchema = schemaJson as unknown as FilterSchema;

const schema: FilterSchema = {
  ...rawSchema,
  enums: {
    ...rawSchema.enums,
    powerFilters: rawSchema.enums.powerFilters.filter(o => SUPPORTED_POWER_FILTERS.has(o.id)),
  },
  categories: rawSchema.categories
    .filter(c => SUPPORTED_CATEGORIES.has(c.id))
    .map(c => ({
      ...c,
      sections: c.sections
        .map(s => ({ ...s, fields: s.fields.filter(f => SUPPORTED_FIELDS.has(f.key)) }))
        .filter(s => s.fields.length > 0),
    }))
    .filter(c => c.sections.length > 0),
};

const ICON_MAP: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  Settings: Icons.Settings, Assignment: Icons.Assignment, LocationOn: Icons.LocationOn,
  Work: Icons.Work, Business: Icons.Business, Layers: Icons.Layers, TrendingUp: Icons.TrendingUp,
  AutoAwesome: Icons.AutoAwesome, Bolt: Icons.Bolt, AutoGraph: Icons.AutoGraph,
  Code: Icons.Code, School: Icons.School, Language: Icons.Language, TextFields: Icons.TextFields,
};

type Vals = Record<string, any>;

interface Props {
  open: boolean;
  onClose: () => void;
  matchCount: number;
  initialValues: Vals;
  onSave: (next: Vals) => void;
}

const lookupEnum = (k?: string | null) => (k ? schema.enums[k] || [] : []);

const inputCls =
  'w-full rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-3 py-2 text-sm outline-none transition-colors focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-muted)]';

const enumChipCls = (on: boolean) =>
  'cursor-pointer select-none rounded-full border px-3 py-1 text-[13px] transition-colors ' +
  (on
    ? 'border-[var(--accent)] bg-[var(--accent-muted)] font-medium text-[var(--accent-strong)]'
    : 'border-[var(--border-default)] bg-[var(--surface)] hover:border-[var(--accent)]');

export function FiltersModal({ open, onClose, matchCount, initialValues, onSave }: Props) {
  const [values, setValues] = useState<Vals>(() => seedDefaults(initialValues));
  const [active, setActive] = useState('general');
  const [search, setSearch] = useState('');
  const [hideInactive, setHideInactive] = useState(false);

  // Resync values whenever the modal opens — initialValues may have changed since last mount
  // (e.g. Grok parsed new filters between opens). Without this, the form shows stale values.
  useEffect(() => {
    if (open) setValues(seedDefaults(initialValues));
  }, [open, initialValues]);

  const cats = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schema.categories.filter(cat => {
      const hasFilters = isCategoryActive(cat.id, values);
      const matches =
        !q ||
        cat.label.toLowerCase().includes(q) ||
        cat.sections.some(s => s.fields.some(f => f.label.toLowerCase().includes(q)));
      if (q && !matches) return false;
      if (hideInactive && !hasFilters && cat.id !== active) return false;
      return true;
    });
  }, [search, hideInactive, values, active]);

  if (!open) return null;
  const activeCat = schema.categories.find(c => c.id === active)!;

  const set = (k: string, v: unknown) => setValues(prev => ({ ...prev, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-6"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] max-h-[800px] w-[min(1140px,96vw)] min-w-[800px] flex-col overflow-hidden rounded-lg border border-[var(--border-default)] bg-[var(--surface)] shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex h-[50px] flex-none items-center justify-between border-b border-[var(--border-default)] bg-[var(--surface)] px-4">
          <div className="text-base font-medium">Edit Your Search Filters</div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[var(--text-muted)]">
              <b className="font-semibold text-[var(--text-primary)]">{compactNumber(matchCount)}</b> matches
            </span>
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--accent-strong)]"
              onClick={() => {
                onSave(values);
                onClose();
              }}
            >
              Save Changes <Icons.ArrowOutward />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-[26%] min-w-[220px] flex-col border-r border-[var(--border-default)] bg-[var(--surface)]">
            <div className="relative p-3">
              <Icons.Search style={{ position: 'absolute', left: 22, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--text-muted)' }} />
              <input
                className="w-full rounded-md border border-[var(--border-default)] bg-[var(--surface-muted)] px-2.5 py-2 pl-9 text-sm outline-none focus:border-[var(--accent)] focus:bg-[var(--surface)]"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search filters"
              />
            </div>
            <nav className="flex-1 overflow-auto px-2 py-1">
              {cats.map(cat => {
                const IconC = ICON_MAP[cat.icon] || Icons.Settings;
                const isActive = active === cat.id;
                return (
                  <div
                    key={cat.id}
                    className={
                      'flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-black/5 ' +
                      (isActive ? 'bg-[var(--accent-muted)]' : '')
                    }
                    onClick={() => setActive(cat.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActive(cat.id);
                      }
                    }}
                  >
                    <span
                      className={
                        'h-5 w-5 flex-none ' +
                        (isActive ? 'text-[var(--accent-strong)]' : 'text-[var(--text-primary)]')
                      }
                    >
                      <IconC />
                    </span>
                    <span
                      className={
                        'flex-1 truncate text-sm ' +
                        (isActive ? 'font-medium text-[var(--accent-strong)]' : '')
                      }
                    >
                      {cat.label}
                    </span>
                    {isCategoryActive(cat.id, values) ? (
                      <span className="h-2 w-2 flex-none rounded-full bg-[var(--accent)]" />
                    ) : null}
                  </div>
                );
              })}
            </nav>
            <label className="mx-3 mb-3 mt-2 flex cursor-pointer items-center gap-2 rounded-md border border-[var(--border-default)] bg-[var(--surface-muted)] px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={hideInactive}
                onChange={e => setHideInactive(e.target.checked)}
                style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
              />
              Hide inactive filters
            </label>
          </aside>

          <main className="flex-1 overflow-auto bg-[var(--surface)] px-8 pb-20 pt-7">
            {activeCat.sections.map((sec, i) => (
              <div key={i}>
                {sec.label ? <h3 className="mb-3 text-base font-semibold">{sec.label}</h3> : null}
                {renderFields(sec.fields, values, set)}
              </div>
            ))}
          </main>
        </div>
      </div>
    </div>
  );
}

function renderFields(fields: FilterField[], values: Vals, set: (k: string, v: unknown) => void) {
  const out: React.ReactNode[] = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    const next = fields[i + 1];
    if (f.key === 'yearsOfExp' && next?.key === 'maxExp') {
      out.push(
        <div className="flex flex-wrap gap-6" key={f.key + '+' + next.key}>
          <div className="flex-1 min-w-[280px]">
            <Field f={f} values={values} set={set} />
          </div>
          <div className="flex-1 min-w-[280px]">
            <Field f={next} values={values} set={set} />
          </div>
        </div>,
      );
      i++;
    } else {
      out.push(<Field key={f.key} f={f} values={values} set={set} />);
    }
  }
  return out;
}

function Field({ f, values, set }: { f: FilterField; values: Vals; set: (k: string, v: unknown) => void }) {
  const active = isFieldActive(f, values);
  const labelCls =
    'mb-1.5 flex items-center gap-1.5 text-[13px] font-medium ' +
    (active ? 'text-[var(--accent-strong)]' : 'text-[var(--text-muted)]');

  if (f.type === 'number') {
    return (
      <div className="mb-6">
        <label className={labelCls}>{f.label}</label>
        <input
          className={inputCls}
          type="number"
          placeholder={f.placeholder || ''}
          value={values[f.key] ?? ''}
          onChange={e => set(f.key, e.target.value === '' ? null : Number(e.target.value))}
        />
      </div>
    );
  }
  if (f.type === 'text') {
    return (
      <div className="mb-6">
        <label className={labelCls}>{f.label}</label>
        <input
          className={inputCls}
          type="text"
          placeholder={f.placeholder || ''}
          value={values[f.key] ?? ''}
          onChange={e => set(f.key, e.target.value || null)}
        />
      </div>
    );
  }
  if (f.type === 'textarea') {
    return (
      <div className="mb-6">
        <label className={labelCls}>{f.label}</label>
        <textarea
          className={inputCls + ' min-h-[90px] resize-y'}
          placeholder={f.placeholder || ''}
          value={values[f.key] ?? ''}
          onChange={e => set(f.key, e.target.value || null)}
        />
      </div>
    );
  }
  if (f.type === 'boolean') {
    return (
      <div className="mb-6">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={values[f.key] === true}
            onChange={e => set(f.key, e.target.checked || null)}
            style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
          />
          <span>{f.label}</span>
          {f.notes ? <span className="ml-1.5 text-[12px] text-[var(--text-muted)]">({f.notes})</span> : null}
        </label>
      </div>
    );
  }
  if (f.type === 'single-select-enum') {
    const opts = lookupEnum(f.options);
    return (
      <div className="mb-6">
        <label className={labelCls}>{f.label}</label>
        <select
          className={inputCls + ' cursor-pointer pr-10 appearance-none'}
          value={values[f.key] ?? ''}
          onChange={e => set(f.key, e.target.value || null)}
        >
          {!opts.find(o => o.id === 'none') ? (
            <option value="">{f.placeholder || 'Select...'}</option>
          ) : null}
          {opts.map(o => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
    );
  }
  if (f.type === 'multi-select-enum' || f.type === 'multi-select-enum-with-mode') {
    const opts = lookupEnum(f.options);
    const cur: string[] = values[f.key] || [];
    return (
      <div className="mb-6">
        <label className={labelCls}>
          {f.label}
          {f.modes ? (
            <ModePill modes={f.modes} value={values[f.modeKey!] || 'any'} onChange={v => set(f.modeKey!, v)} />
          ) : null}
        </label>
        {opts.length ? (
          <div className="flex flex-wrap gap-1.5">
            {opts.map(o => {
              const on = cur.includes(o.id);
              return (
                <div
                  key={o.id}
                  className={enumChipCls(on)}
                  onClick={() => set(f.key, on ? cur.filter(x => x !== o.id) : [...cur, o.id])}
                >
                  {o.name}
                </div>
              );
            })}
          </div>
        ) : (
          <TagInputBare placeholder={f.placeholder || ''} />
        )}
      </div>
    );
  }
  if (f.type === 'multi-select-async') {
    return (
      <div className="mb-6">
        <label className={labelCls}>{f.label}</label>
        <TagInputBare placeholder={f.placeholder || ''} />
      </div>
    );
  }
  if (f.type === 'multi-select-tag' || f.type === 'autocomplete-entity') {
    return (
      <div className="mb-6">
        <label className={labelCls}>
          {f.label}
          {f.modes ? (
            <ModePill modes={f.modes} value={values[f.modeKey!] || 'any'} onChange={v => set(f.modeKey!, v)} />
          ) : null}
        </label>
        <TagInput value={values[f.key] || []} onChange={v => set(f.key, v)} placeholder={f.placeholder || ''} />
      </div>
    );
  }
  if (f.type === 'date-range') {
    const v = values[f.key] || {};
    return (
      <div className="mb-6">
        <label className={labelCls}>{f.label}</label>
        <div className="flex items-center gap-2">
          <input
            className={inputCls}
            type="date"
            value={v.start || ''}
            onChange={e => set(f.key, { ...v, start: e.target.value || null })}
          />
          <input
            className={inputCls}
            type="date"
            value={v.end || ''}
            onChange={e => set(f.key, { ...v, end: e.target.value || null })}
          />
        </div>
      </div>
    );
  }
  if (f.type === 'exclude-profiles-group') {
    const types = lookupEnum('excludeProfileTypes');
    const scopes = lookupEnum('excludeProfileScopes');
    const windows = lookupEnum('timeWindows');
    const cur = values[f.key] || {};
    return (
      <div className="mb-6">
        <label className={labelCls}>{f.label}</label>
        {types.map(t => {
          const it = cur[t.id] || { enabled: false, by: 'org-project', when: 'none' };
          return (
            <div key={t.id} className="mb-2 flex flex-wrap items-center gap-2.5">
              <label className="flex min-w-[110px] cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={it.enabled}
                  onChange={e => set(f.key, { ...cur, [t.id]: { ...it, enabled: e.target.checked } })}
                  style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                />
                {t.name}
              </label>
              <span className="text-sm text-[var(--text-muted)]">by</span>
              <select
                className="cursor-pointer rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-2.5 py-1.5 text-[13px] disabled:cursor-not-allowed disabled:bg-[var(--surface-muted)] disabled:text-[var(--text-muted)] disabled:opacity-70"
                disabled={!it.enabled}
                value={it.by}
                onChange={e => set(f.key, { ...cur, [t.id]: { ...it, by: e.target.value } })}
              >
                {scopes.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select
                className="cursor-pointer rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-2.5 py-1.5 text-[13px] disabled:cursor-not-allowed disabled:bg-[var(--surface-muted)] disabled:text-[var(--text-muted)] disabled:opacity-70"
                disabled={!it.enabled}
                value={it.when}
                onChange={e => set(f.key, { ...cur, [t.id]: { ...it, when: e.target.value } })}
              >
                {windows.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    );
  }
  if (f.type === 'locations-group') {
    return (
      <div className="mb-6">
        <div className="mb-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label className={labelCls}>{f.label}</label>
            <RadiusPill value={values.locationRadius || '25mi'} onChange={v => set('locationRadius', v)} />
          </div>
          <div className="flex gap-3.5 text-[13px]">
            <a className="cursor-pointer text-[var(--text-link)] hover:underline" onClick={() => set('locations', [])}>
              Clear all
            </a>
            <a className="cursor-pointer text-[var(--text-link)] hover:underline">⊞ Select Preset</a>
          </div>
        </div>
        <LocationInput value={values.locations || []} onChange={v => set('locations', v)} />
      </div>
    );
  }
  return (
    <div className="mb-6">
      <label className={labelCls}>
        {f.label} <span className="text-[12px] text-[var(--text-muted)]">(unsupported: {f.type})</span>
      </label>
    </div>
  );
}

function ModePill({ modes, value, onChange }: { modes: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <span
      className="ml-2 inline-flex items-center rounded-full bg-[var(--surface-tertiary)] p-0.5 text-[12px]"
      onClick={e => e.preventDefault()}
    >
      {modes.map(m => (
        <button
          key={m}
          type="button"
          className={
            'rounded-full border-0 bg-transparent px-2.5 py-0.5 text-[12px] transition-colors ' +
            (value === m
              ? 'bg-[var(--surface)] font-medium text-[var(--text-primary)] shadow-sm'
              : 'text-[var(--text-muted)]')
          }
          onClick={ev => {
            ev.preventDefault();
            ev.stopPropagation();
            onChange(m);
          }}
        >
          {m === 'any' ? 'Match Any' : 'Match All'}
        </button>
      ))}
    </span>
  );
}

interface TagItem {
  id?: string;
  label: string;
  kind?: string;
}
function TagInput({ value, onChange, placeholder }: { value: (string | TagItem)[]; onChange: (v: any[]) => void; placeholder?: string }) {
  const [text, setText] = useState('');
  return (
    <div
      className="flex min-h-[38px] cursor-text flex-wrap gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] p-1.5 focus-within:border-[var(--accent)]"
      onClick={e => (e.currentTarget.querySelector('input') as HTMLInputElement)?.focus()}
    >
      {value.map((t, i) => {
        const label = typeof t === 'string' ? t : t.label;
        return (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-sm bg-[var(--surface-tertiary)] px-2 py-0.5 text-[13px]"
          >
            {label}
            <span
              className="cursor-pointer pl-0.5 text-base leading-none text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              onClick={e => {
                e.stopPropagation();
                onChange(value.filter((_, j) => j !== i));
              }}
            >
              ×
            </span>
          </span>
        );
      })}
      <input
        className="min-w-[140px] flex-1 border-0 bg-transparent px-1.5 py-1 text-sm outline-none"
        value={text}
        placeholder={value.length ? '' : placeholder}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && text.trim()) {
            e.preventDefault();
            onChange([...value, text.trim()]);
            setText('');
          }
          if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
        }}
      />
    </div>
  );
}

function TagInputBare({ placeholder }: { placeholder: string }) {
  return (
    <div className="flex min-h-[38px] flex-wrap gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] p-1.5">
      <input
        className="min-w-[140px] flex-1 border-0 bg-transparent px-1.5 py-1 text-sm outline-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function RadiusPill({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const opts = lookupEnum('locationRadius');
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--surface-tertiary)] px-2.5 py-0.5 text-[12px]">
      <select
        className="cursor-pointer appearance-none border-0 bg-transparent pr-3.5 text-[12px] outline-none"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {opts.map(o => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </span>
  );
}

function LocationInput({ value, onChange }: { value: (string | TagItem)[]; onChange: (v: (string | TagItem)[]) => void }) {
  const [text, setText] = useState('');
  // Grok parser emits plain strings (`["Hong Kong"]`); the user-add path emits TagItem.
  // Render both shapes uniformly; preserve original type when removing so the filter
  // payload sent to the backend matches Grok's expected shape.
  return (
    <div
      className="flex min-h-[38px] cursor-text flex-wrap gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] p-1.5 focus-within:border-[var(--accent)]"
      onClick={e => (e.currentTarget.querySelector('input') as HTMLInputElement)?.focus()}
    >
      {value.map((t, i) => {
        const label = typeof t === 'string' ? t : t.label;
        return (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-sm bg-[var(--surface-tertiary)] px-2 py-0.5 text-[13px]"
          >
            <Icons.CityPin style={{ width: 12, height: 12, marginRight: 2 }} />
            {label}
            <span
              className="cursor-pointer pl-0.5 text-base leading-none text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              onClick={e => {
                e.stopPropagation();
                onChange(value.filter((_, j) => j !== i));
              }}
            >
              ×
            </span>
          </span>
        );
      })}
      <input
        className="min-w-[140px] flex-1 border-0 bg-transparent px-1.5 py-1 text-sm outline-none"
        value={text}
        placeholder={value.length ? '' : 'Examples: San Francisco / United States / NYC / California'}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && text.trim()) {
            e.preventDefault();
            // Use plain string to match Grok's output shape — the backend csv() helper handles both.
            onChange([...value, text.trim()]);
            setText('');
          }
        }}
      />
    </div>
  );
}

function isFieldActive(f: FilterField, values: Vals): boolean {
  const v = values[f.key];
  if (f.type === 'exclude-profiles-group') {
    return Object.values(values[f.key] || {}).some((x: any) => x?.enabled);
  }
  if (f.type === 'locations-group') {
    return (values.locations || []).length > 0;
  }
  if (Array.isArray(v)) return v.length > 0;
  if (v == null || v === '' || v === 'none') return false;
  return true;
}
function isCategoryActive(catId: string, values: Vals): boolean {
  const cat = schema.categories.find(c => c.id === catId);
  if (!cat) return false;
  return cat.sections.some(s => s.fields.some(f => isFieldActive(f, values)));
}
function compactNumber(n: number): string {
  if (n >= 1000) return Math.round(n / 100) / 10 + 'k';
  return String(n);
}
function seedDefaults(v: Vals): Vals {
  const out = { ...v };
  for (const cat of schema.categories) for (const sec of cat.sections) for (const f of sec.fields) {
    if (f.modeKey && out[f.modeKey] == null) out[f.modeKey] = 'any';
  }
  return out;
}
